import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/Button.jsx';
import { DisasterCard } from '../components/DisasterCard.jsx';
import {
  revealVariableRolls,
  ackVariableRolls,
  revealDisasters,
  resolveYear as resolveYearAction
} from '../state/actions.js';
import { PHASE } from '../state/phases.js';
import { configForState, isAdvancedMode } from '../state/modeConfig.js';
import { impactfulDrawnDisasters } from '../state/disasterImpact.js';
import { decodeImages } from '../utils/preloadImages.js';
import { useAudio } from '../audio/AudioProvider.jsx';

const STATUS_LINES = [
  'Optimising grid topology',
  'Balancing load curve',
  'Calculating carbon ledger'
];

const TIMING = {
  shuttersClose: 700,
  cogIn: 300,
  statusOne: 200,
  statusGap: 1800,
  tickHold: 1100,
  shuttersOpen: 600
};

const COG_PATH =
  'M50 6 L54 6 L55 16 L60 17 L65 8 L68 10 L66 19 L70 22 L78 16 L80 19 L74 27 L77 32 L86 32 L86 36 L77 38 L77 43 L86 46 L84 50 L75 50 L74 55 L82 60 L80 64 L70 60 L66 64 L70 73 L66 75 L60 67 L55 70 L55 80 L50 80 L46 80 L45 70 L40 67 L34 75 L31 73 L33 64 L29 60 L20 64 L18 60 L26 55 L23 50 L14 50 L14 46 L23 43 L23 38 L14 36 L14 32 L23 32 L26 27 L20 19 L22 16 L30 22 L34 19 L32 10 L35 8 L40 17 L45 16 L46 6 Z';

function rehydrateDrawnDisaster(id, severityByDisasterId, disasterById) {
  const card = disasterById[id];
  const severityRoll = severityByDisasterId?.[id];
  if (!severityRoll || !card?.twoTierSeverity) return card;
  return {
    ...card,
    effect: card.severity[severityRoll],
    severityRoll,
    isSevere: severityRoll === 'severe'
  };
}

export function YearTransitionScreen({ state, dispatch, onComplete }) {
  const [stage, setStage] = useState('closing');
  const [statusIndex, setStatusIndex] = useState(-1);
  const [activePopup, setActivePopup] = useState(null);
  const [popupQueue, setPopupQueue] = useState(null);
  const [popupImagesReady, setPopupImagesReady] = useState(false);
  const dispatchedSetupRef = useRef(false);
  const dispatchedResolveRef = useRef(false);
  const completedRef = useRef(false);
  const subsidyAtStartRef = useRef(state.subsidyActive);
  const yearLabel = state.year;
  const { playCue } = useAudio();

  useEffect(() => {
    if (stage !== 'processing') return;
    if (state.phase !== PHASE.AWAITING_VARIABLE_ROLLS) return;
    if (dispatchedSetupRef.current) return;
    dispatchedSetupRef.current = true;
    dispatch(revealVariableRolls());
    dispatch(ackVariableRolls());
    dispatch(revealDisasters());
  }, [stage, state.phase, dispatch]);

  useEffect(() => {
    if (popupQueue !== null) return;
    if (
      state.phase !== PHASE.RESOLVING &&
      state.phase !== PHASE.BETWEEN_YEARS
    ) {
      return;
    }
    const config = configForState(state);
    const drawn = state.drawnDisasterIds.map((id) =>
      rehydrateDrawnDisaster(id, state.severityByDisasterId, config.disasterById)
    );
    const impactful = impactfulDrawnDisasters(drawn, {
      portfolio: state.portfolio,
      plantsById: config.plantsById,
      subsidyActive: subsidyAtStartRef.current
    });
    setPopupQueue(impactful);
  }, [
    popupQueue,
    state.phase,
    state.drawnDisasterIds,
    state.portfolio,
    state.severityByDisasterId
  ]);

  useEffect(() => {
    if (popupQueue === null) return;
    let cancelled = false;
    const urls = [
      ...new Set(
        popupQueue.map((d) => d.imageSrc ?? `/disaster-images/${d.id}.webp`)
      )
    ];

    if (urls.length === 0) {
      setPopupImagesReady(true);
      return;
    }

    setPopupImagesReady(false);
    decodeImages(urls).then(() => {
      if (!cancelled) setPopupImagesReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [popupQueue]);

  useEffect(() => {
    const t1 = setTimeout(() => setStage('processing'), TIMING.shuttersClose);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (stage !== 'processing') return;
    if (popupQueue === null) return;
    if (activePopup) return;

    if (statusIndex < 0) {
      const t = setTimeout(() => setStatusIndex(0), TIMING.statusOne);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      const popupAt = statusIndex;
      if (popupAt < popupQueue.length) {
        if (!popupImagesReady) return;
        playCue('disasterReveal');
        setActivePopup({
          disaster: popupQueue[popupAt],
          remainingIndex: popupAt
        });
        return;
      }
      const next = statusIndex + 1;
      if (next < STATUS_LINES.length) {
        setStatusIndex(next);
      } else {
        setStatusIndex(STATUS_LINES.length);
        setStage('tick');
      }
    }, TIMING.statusGap);
    return () => clearTimeout(t);
  }, [stage, statusIndex, popupQueue, popupImagesReady, activePopup]);

  useEffect(() => {
    if (stage !== 'tick') return;
    if (dispatchedResolveRef.current) return;
    dispatchedResolveRef.current = true;
    const tResolve = setTimeout(() => {
      dispatch(resolveYearAction());
    }, 250);
    const tOpen = setTimeout(() => {
      setStage('opening');
    }, TIMING.tickHold);
    return () => {
      clearTimeout(tResolve);
      clearTimeout(tOpen);
    };
  }, [stage, dispatch]);

  useEffect(() => {
    if (stage !== 'opening') return;
    if (completedRef.current) return;
    completedRef.current = true;
    const t = setTimeout(() => {
      setStage('done');
      onComplete?.();
    }, TIMING.shuttersOpen);
    return () => clearTimeout(t);
  }, [stage, onComplete]);

  function acknowledgePopup() {
    if (!activePopup) return;
    const next = activePopup.remainingIndex + 1;
    setActivePopup(null);
    if (next < STATUS_LINES.length) {
      setStatusIndex(next);
    } else {
      setStatusIndex(STATUS_LINES.length);
      setStage('tick');
    }
  }

  return (
    <div
      className="year-transition"
      data-stage={stage}
      data-paused={activePopup ? 'true' : 'false'}
      role="status"
      aria-live="polite"
      aria-label={`Resolving year ${yearLabel}`}
    >
      <div className="year-transition-shutter year-transition-shutter-l" aria-hidden="true" />
      <div className="year-transition-shutter year-transition-shutter-r" aria-hidden="true" />
      <div className="year-transition-seam" aria-hidden="true" />

      <div className="year-transition-processing">
        <div className="year-transition-eyebrow">
          {stage === 'tick' || stage === 'opening' || stage === 'done'
            ? `Year ${yearLabel} complete`
            : `End of Year ${yearLabel}, processing`}
        </div>

        <div className="year-transition-cog-wrap">
          <svg
            className="year-transition-cog"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <path d={COG_PATH} />
            <circle className="year-transition-cog-core" cx="50" cy="50" r="14" />
            <circle cx="50" cy="50" r="14" />
            <circle cx="50" cy="50" r="6" />
          </svg>
          <div className="year-transition-tick" aria-hidden="true">
            <svg viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" />
              <path d="M30 52 L44 66 L72 36" />
            </svg>
          </div>
        </div>

        <div className="year-transition-status">
          {STATUS_LINES.map((line, i) => {
            const isActive = i === statusIndex;
            return (
              <div
                key={line}
                className={`year-transition-status-line${isActive ? ' year-transition-status-line-active' : ''}`}
                data-testid={`transition-status-${i}`}
              >
                <span className="year-transition-dot" />
                <span className="year-transition-text">{line}</span>
                <span className="year-transition-ellipsis" aria-hidden="true" />
              </div>
            );
          })}
          <div
            className={`year-transition-status-line year-transition-status-line-final${statusIndex >= STATUS_LINES.length ? ' year-transition-status-line-active' : ''}`}
            data-testid="transition-status-complete"
          >
            <span className="year-transition-dot year-transition-dot-final" />
            <span className="year-transition-text">Year {yearLabel} complete</span>
          </div>
        </div>
      </div>

      {activePopup ? (
        <div
          className="year-transition-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transition-popup-heading"
          data-testid="transition-disaster-popup"
        >
          <div className="year-transition-popup-inner">
            <div className="year-transition-popup-eyebrow">
              System stress, Year {yearLabel}
            </div>
            <h2
              id="transition-popup-heading"
              className="year-transition-popup-heading"
            >
              {activePopup.disaster.name}
            </h2>
            <DisasterCard
              disaster={activePopup.disaster}
              year={state.year}
              advanced={isAdvancedMode(state)}
              testId={`transition-popup-card-${activePopup.disaster.id}`}
            />
            <div className="year-transition-popup-actions">
              <Button
                onClick={acknowledgePopup}
                testId="transition-popup-acknowledge"
              >
                Acknowledge and continue
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
